import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useState, type FormEvent } from "react";
import { useLocation } from "wouter";
import DoctorChooser from "../../components/DoctorChooser.tsx";
import useUser from "../users/hooks/useUser";
import useOwner from "../users/hooks/useOwner";
import {
  tasksApi,
  type Task,
  type TaskSecretary,
  type TaskStatus,
  type TaskStep,
  type TaskType,
} from "./tasksApi";

const statusLabels: Record<TaskStatus, string> = {
  todo: "À faire",
  waiting: "En attente",
  done: "Terminée",
};

const typeLabels: Record<TaskType, string> = {
  call: "Appel",
  question: "Question",
};

function taskListKey(doctorId: string | null) {
  return ["cadendar-tasks", doctorId];
}

function taskDetailKey(owner: string | null, taskId: string | null) {
  return ["cadendar-task", owner, taskId];
}

function formatDate(value: string) {
  return DateTime.fromISO(value).toFormat("dd/MM/yyyy HH:mm");
}

function parseTaskType(value: FormDataEntryValue | null) {
  return value === "call" || value === "question" ? value : null;
}

function parseTaskStatus(value: FormDataEntryValue | null) {
  return value === "todo" || value === "waiting" || value === "done" ? value : null;
}

const TasksPage = () => {
  const owner = useOwner();
  const user = useUser();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<TaskStatus | "">("");
  const [typeFilter, setTypeFilter] = useState<TaskType | "">("");
  const [secretaryFilter, setSecretaryFilter] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const secretariesQuery = useQuery({
    queryKey: ["cadendar-task-secretaries", owner],
    queryFn: () => {
      if (!owner) throw new Error("Aucun praticien n’est sélectionné.");
      return tasksApi.listSecretaries(owner);
    },
    enabled: Boolean(owner),
  });
  const tasksQuery = useQuery({
    queryKey: ["cadendar-tasks", owner, statusFilter, typeFilter, secretaryFilter],
    queryFn: () => {
      if (!owner) throw new Error("Aucun praticien n’est sélectionné.");
      return tasksApi.listTasks({
        doctorId: owner,
        status: statusFilter || undefined,
        taskType: typeFilter || undefined,
        assignedSecretaryId: secretaryFilter || undefined,
      });
    },
    enabled: Boolean(owner),
    refetchInterval: 10_000,
  });
  const tasks = tasksQuery.data ?? [];
  const selectedTaskIsForOwner = tasks.some((task) => task.id === selectedTaskId);
  const taskQuery = useQuery({
    queryKey: taskDetailKey(owner, selectedTaskId),
    queryFn: () => {
      if (!selectedTaskId) throw new Error("Aucune tâche n’est sélectionnée.");
      return tasksApi.getTask(selectedTaskId);
    },
    enabled: Boolean(owner && selectedTaskId && selectedTaskIsForOwner),
  });

  const refreshTasks = async (taskId?: string) => {
    await queryClient.invalidateQueries({ queryKey: taskListKey(owner) });
    if (taskId) {
      await queryClient.invalidateQueries({ queryKey: taskDetailKey(owner, taskId) });
    }
  };
  const createTaskMutation = useMutation({
    mutationFn: tasksApi.createTask,
    onSuccess: async (task) => {
      setSelectedTaskId(task.id);
      setIsCreateOpen(false);
      await refreshTasks(task.id);
    },
  });
  const updateTaskMutation = useMutation({
    mutationFn: tasksApi.updateTask,
    onSuccess: async (task) => {
      await refreshTasks(task.id);
    },
  });
  const addStepMutation = useMutation({
    mutationFn: tasksApi.addStep,
    onSuccess: async (task) => {
      await refreshTasks(task.id);
    },
  });

  const secretaries = secretariesQuery.data ?? [];
  const selectedTask = taskQuery.data?.task.doctorId === owner ? taskQuery.data.task : null;
  const isSaving =
    createTaskMutation.isPending || updateTaskMutation.isPending || addStepMutation.isPending;
  const mutationError =
    createTaskMutation.error ?? updateTaskMutation.error ?? addStepMutation.error;

  const secretaryName = (secretaryId: string | null) => {
    if (!secretaryId) return "Non assignée";
    return secretaries.find((secretary) => secretary.id === secretaryId)?.fullname ?? "Secrétaire inconnue";
  };

  const submitCreateTask = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!owner) return;
    const formData = new FormData(event.currentTarget);
    const title = formData.get("title");
    const description = formData.get("description");
    const taskType = parseTaskType(formData.get("taskType"));
    const status = parseTaskStatus(formData.get("status"));
    const assignedSecretaryId = formData.get("assignedSecretaryId");
    if (typeof title !== "string" || typeof description !== "string" || !taskType || !status) return;
    createTaskMutation.mutate({
      doctorId: owner,
      title,
      description,
      taskType,
      status,
      assignedSecretaryId: typeof assignedSecretaryId === "string" && assignedSecretaryId ? assignedSecretaryId : undefined,
    });
  };

  const submitUpdateTask = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedTask) return;
    const formData = new FormData(event.currentTarget);
    const title = formData.get("title");
    const description = formData.get("description");
    const taskType = parseTaskType(formData.get("taskType"));
    if (typeof title !== "string" || typeof description !== "string" || !taskType) return;
    const updates = {
      taskId: selectedTask.id,
      expectedUpdatedAt: selectedTask.updatedAt,
      ...(title !== selectedTask.title ? { title } : {}),
      ...(description !== selectedTask.description ? { description } : {}),
      ...(taskType !== selectedTask.taskType ? { taskType } : {}),
    } satisfies Parameters<typeof tasksApi.updateTask>[0];
    if (Object.keys(updates).length > 2) {
      updateTaskMutation.mutate(updates);
    }
  };

  const submitComment = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedTask) return;
    const formData = new FormData(event.currentTarget);
    const comment = formData.get("comment");
    if (typeof comment !== "string" || !comment.trim()) return;
    const form = event.currentTarget;
    addStepMutation.mutate(
      { taskId: selectedTask.id, expectedUpdatedAt: selectedTask.updatedAt, comment },
      { onSuccess: () => form.reset() },
    );
  };

  if (!owner) {
    return (
      <div className="space-y-4 p-6 text-slate-600">
        <p>Sélectionnez un praticien pour consulter ses tâches.</p>
        {user?.tag === "secretary" ? <DoctorChooser /> : null}
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-100 p-4 text-slate-900">
      <div className="mx-auto max-w-7xl space-y-4">
        <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] text-blue-700 uppercase">Suivi d’équipe</p>
              <h1 className="mt-1 text-2xl font-semibold">Tâches</h1>
              <p className="mt-1 text-sm text-slate-600">Appels et questions à traiter avec les secrétaires du cabinet.</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {user?.tag === "secretary" ? <DoctorChooser /> : null}
              <button type="button" className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" onClick={() => navigate("/")}>Agenda</button>
              <button
                type="button"
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
                onClick={() => setIsCreateOpen(true)}
                disabled={isSaving}
              >
                Nouvelle tâche
              </button>
            </div>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <select value={statusFilter} onChange={(event) => setStatusFilter(parseTaskStatus(event.target.value) ?? "")} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
              <option value="">Tous les états</option>
              {Object.entries(statusLabels).map(([status, label]) => <option key={status} value={status}>{label}</option>)}
            </select>
            <select value={typeFilter} onChange={(event) => setTypeFilter(parseTaskType(event.target.value) ?? "")} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
              <option value="">Tous les types</option>
              {Object.entries(typeLabels).map(([type, label]) => <option key={type} value={type}>{label}</option>)}
            </select>
            <select value={secretaryFilter} onChange={(event) => setSecretaryFilter(event.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
              <option value="">Toutes les secrétaires</option>
              {secretaries.map((secretary) => <option key={secretary.id} value={secretary.id}>{secretary.fullname}</option>)}
            </select>
          </div>
        </section>

        {mutationError ? <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{mutationError.message}</div> : null}
        {tasksQuery.error ? <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">Impossible de charger les tâches : {tasksQuery.error.message}</div> : null}
        {secretariesQuery.error ? <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">Impossible de charger les secrétaires : {secretariesQuery.error.message}</div> : null}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(360px,1.1fr)]">
          <section className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
            <div className="border-b border-slate-200 px-5 py-3 text-sm font-medium text-slate-600">{tasks.length} tâche{tasks.length > 1 ? "s" : ""}</div>
            {tasksQuery.isLoading ? <p className="p-5 text-sm text-slate-500">Chargement des tâches...</p> : null}
            {!tasksQuery.isLoading && tasks.length === 0 ? <p className="p-5 text-sm text-slate-500">Aucune tâche ne correspond aux filtres.</p> : null}
            <div className="divide-y divide-slate-100">
              {tasks.map((task) => (
                <TaskRow key={task.id} task={task} secretaryName={secretaryName(task.assignedSecretaryId)} selected={task.id === selectedTaskId} onSelect={() => setSelectedTaskId(task.id)} />
              ))}
            </div>
          </section>

          <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            {taskQuery.isLoading ? <p className="text-sm text-slate-500">Chargement de la tâche...</p> : null}
            {!selectedTaskId ? <p className="text-sm text-slate-500">Sélectionnez une tâche pour consulter son historique.</p> : null}
            {taskQuery.error ? <p className="text-sm text-red-700">Impossible de charger cette tâche : {taskQuery.error.message}</p> : null}
            {selectedTask ? (
              <TaskDetail
                task={selectedTask}
                steps={taskQuery.data?.steps ?? []}
                secretaries={secretaries}
                secretaryName={secretaryName}
                isSaving={isSaving}
                onUpdate={submitUpdateTask}
                onComment={submitComment}
                onStatusChange={(status) => addStepMutation.mutate({ taskId: selectedTask.id, expectedUpdatedAt: selectedTask.updatedAt, status })}
                onAssigneeChange={(secretaryId) => addStepMutation.mutate(secretaryId ? { taskId: selectedTask.id, expectedUpdatedAt: selectedTask.updatedAt, assignedSecretaryId: secretaryId } : { taskId: selectedTask.id, expectedUpdatedAt: selectedTask.updatedAt, unassign: true })}
              />
            ) : null}
          </section>
        </div>
      </div>

      {isCreateOpen ? (
        <TaskFormModal secretaries={secretaries} isSaving={isSaving} onClose={() => setIsCreateOpen(false)} onSubmit={submitCreateTask} />
      ) : null}
    </div>
  );
};

function TaskRow({ task, secretaryName, selected, onSelect }: { task: Task; secretaryName: string; selected: boolean; onSelect: () => void }) {
  return (
    <button type="button" className={`w-full px-5 py-4 text-left hover:bg-slate-50 ${selected ? "bg-blue-50" : ""}`} onClick={onSelect}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap gap-2 text-xs font-medium"><span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">{typeLabels[task.taskType]}</span><span className="rounded-full bg-blue-100 px-2 py-1 text-blue-800">{statusLabels[task.status]}</span></div>
          <p className="mt-2 font-semibold text-slate-900">{task.title}</p>
          <p className="mt-1 text-sm text-slate-500">{secretaryName}</p>
        </div>
        <time className="shrink-0 text-xs text-slate-400">{formatDate(task.updatedAt)}</time>
      </div>
    </button>
  );
}

function TaskDetail({ task, steps, secretaries, secretaryName, isSaving, onUpdate, onComment, onStatusChange, onAssigneeChange }: { task: Task; steps: TaskStep[]; secretaries: TaskSecretary[]; secretaryName: (secretaryId: string | null) => string; isSaving: boolean; onUpdate: (event: FormEvent<HTMLFormElement>) => void; onComment: (event: FormEvent<HTMLFormElement>) => void; onStatusChange: (status: TaskStatus) => void; onAssigneeChange: (secretaryId: string) => void }) {
  return (
    <div className="space-y-5" key={`${task.id}-${task.updatedAt}`}>
      <form className="space-y-3" onSubmit={onUpdate}>
        <div className="flex flex-wrap gap-3"><select name="taskType" defaultValue={task.taskType} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"><option value="call">Appel</option><option value="question">Question</option></select><select value={task.status} onChange={(event) => { const status = parseTaskStatus(event.target.value); if (status) onStatusChange(status); }} disabled={isSaving} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">{Object.entries(statusLabels).map(([status, label]) => <option key={status} value={status}>{label}</option>)}</select><select value={task.assignedSecretaryId ?? ""} onChange={(event) => onAssigneeChange(event.target.value)} disabled={isSaving} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"><option value="">Non assignée</option>{secretaries.map((secretary) => <option key={secretary.id} value={secretary.id}>{secretary.fullname}</option>)}</select></div>
        <input name="title" defaultValue={task.title} required className="w-full rounded-lg border border-slate-300 px-3 py-2 text-lg font-semibold" />
        <textarea name="description" defaultValue={task.description} rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Contexte de la tâche" />
        <div className="flex items-center justify-between gap-3"><span className="text-xs text-slate-500">Assignée à {secretaryName(task.assignedSecretaryId)}</span><button type="submit" disabled={isSaving} className="rounded-lg border border-blue-700 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50">{isSaving ? "Enregistrement..." : "Enregistrer"}</button></div>
      </form>
      <div className="border-t border-slate-200 pt-5"><h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Historique</h2><form className="mt-3 flex gap-2" onSubmit={onComment}><input name="comment" required placeholder="Ajouter un commentaire" className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" /><button type="submit" disabled={isSaving} className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Ajouter</button></form><ol className="mt-5 space-y-4 border-l border-slate-200 pl-4">{steps.map((step) => <li key={step.id} className="relative text-sm"><span className="absolute -left-[21px] top-1 size-2.5 rounded-full bg-blue-600" /><p className="font-medium text-slate-800">{step.kind === "created" ? "Tâche créée" : step.kind === "comment" ? "Commentaire ajouté" : "Tâche mise à jour"}</p><p className="text-xs text-slate-500">{step.authorFullname} · {formatDate(step.createdAt)} · {statusLabels[step.status]} · {secretaryName(step.assignedSecretaryId)}</p>{step.comment ? <p className="mt-1 whitespace-pre-wrap text-slate-700">{step.comment}</p> : null}</li>)}</ol></div>
    </div>
  );
}

function TaskFormModal({ secretaries, isSaving, onClose, onSubmit }: { secretaries: TaskSecretary[]; isSaving: boolean; onClose: () => void; onSubmit: (event: FormEvent<HTMLFormElement>) => void }) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4"><form className="w-full max-w-xl space-y-4 rounded-2xl bg-white p-6 shadow-xl" onSubmit={onSubmit}><div className="flex items-start justify-between gap-3"><div><h2 className="text-xl font-semibold">Nouvelle tâche</h2><p className="mt-1 text-sm text-slate-500">Créez un appel ou une question à suivre.</p></div><button type="button" onClick={onClose} disabled={isSaving} className="text-sm text-slate-500">Fermer</button></div><input name="title" required autoFocus placeholder="Titre de la tâche" className="w-full rounded-lg border border-slate-300 px-3 py-2" /><textarea name="description" rows={4} placeholder="Description ou contexte" className="w-full rounded-lg border border-slate-300 px-3 py-2" /><div className="grid gap-3 sm:grid-cols-3"><select name="taskType" defaultValue="call" className="rounded-lg border border-slate-300 px-3 py-2 text-sm"><option value="call">Appel</option><option value="question">Question</option></select><select name="status" defaultValue="todo" className="rounded-lg border border-slate-300 px-3 py-2 text-sm"><option value="todo">À faire</option><option value="waiting">En attente</option><option value="done">Terminée</option></select><select name="assignedSecretaryId" defaultValue="" className="rounded-lg border border-slate-300 px-3 py-2 text-sm"><option value="">Non assignée</option>{secretaries.map((secretary) => <option key={secretary.id} value={secretary.id}>{secretary.fullname}</option>)}</select></div><div className="flex justify-end gap-3"><button type="button" onClick={onClose} disabled={isSaving} className="rounded-lg px-3 py-2 text-sm text-slate-600">Annuler</button><button type="submit" disabled={isSaving} className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{isSaving ? "Création..." : "Créer la tâche"}</button></div></form></div>;
}

export default TasksPage;
